import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  Button,
  Avatar,
  TablePagination, // ✅ Changed from Pagination to TablePagination
} from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, message as AntMessage } from "antd";
import { Search } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getAllProducts,
  deleteProduct,
} from "../redux/slices/sellerProductSlice";

const SellerProduct = () => {
  const dispatch = useDispatch();
  const { allProducts, loading } = useSelector((state) => state.products);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0); // ✅ Start from 0 for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(5); // ✅ Default 10 rows

  useEffect(() => {
    if (allProducts?.length === 0) {
      dispatch(getAllProducts());
    }
  }, [dispatch, allProducts?.length]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this product?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteProduct(id)).unwrap();
          AntMessage.success("Product deleted successfully");
        } catch (err) {
          AntMessage.error(err || "Failed to delete product");
        }
      },
    });
  };

  // Filter products based on search term
  const filteredProducts = allProducts?.filter((product) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = product.name?.toLowerCase().includes(term);
    const phoneMatch = product.user?.phone?.toLowerCase().includes(term);
    return nameMatch || phoneMatch;
  });

  // Paginate filtered products
  const paginatedProducts = filteredProducts?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const title = "Seller Products Report";
    const currentDate = new Date().toLocaleString();

    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${currentDate}`, 14, 22);

    const tableColumn = [
      "#",
      "Product Name",
      "Price",
      "Category",
      "User Name",
      "User Phone",
    ];
    const tableRows = [];

    filteredProducts.forEach((product, index) => {
      const rowData = [
        index + 1,
        product.name || "-",
        product.price || "-",
        product.category?.name || "-",
        product.user?.name || "-",
        product.user?.phone || "-",
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      margin: { top: 30 },
    });

    doc.save(`Seller_Products_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <Box>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Manage Seller Products
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          width: "fit-content",
        }}
      >
        <MUILink
          component={Link}
          to="/admin-dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Seller Products</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by product name & user phone number"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search sx={{ color: "primary.main" }} />
              </IconButton>
            ),
          }}
          sx={{
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
        >
          Export
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Image</strong>
                </TableCell>
                <TableCell>
                  <strong>Product Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Price</strong>
                </TableCell>
                <TableCell>
                  <strong>Category</strong>
                </TableCell>
                <TableCell>
                  <strong>GST</strong>
                </TableCell>
                <TableCell>
                  <strong>Buyer Category</strong>
                </TableCell>
                <TableCell>
                  <strong>Discount</strong>
                </TableCell>
                <TableCell>
                  <strong>User Name</strong>
                </TableCell>
                <TableCell>
                  <strong>User Phone</strong>
                </TableCell>
                <TableCell>
                  <strong>Created At</strong>
                </TableCell>
                <TableCell>
                  <strong>Action</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts?.map((product, index) => (
                <TableRow key={product._id}>
                  <TableCell>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell>
                    <Avatar
                      src={product.image}
                      alt={product.name}
                      variant="rounded"
                      sx={{ width: 50, height: 50 }}
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>₹{product.price}</TableCell>
                  <TableCell>{product.category?.name || "-"}</TableCell>
                  <TableCell>{product.category?.gst || "-"}</TableCell>
                  <TableCell>{product.buyerCategory?.name || "-"}</TableCell>
                  <TableCell>
                    {product.buyerCategory?.discount || "-"}
                  </TableCell>
                  <TableCell>{product.user?.name || "-"}</TableCell>
                  <TableCell>{product.user?.phone || "-"}</TableCell>
                  <TableCell>
                    {new Date(product.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDelete(product._id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredProducts?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No Seller Products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* ✅ TablePagination Component - Same as your SellerCategories */}
          <TablePagination
            component="div"
            count={filteredProducts?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default SellerProduct;